import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import HourglassEmpty from '@mui/icons-material/HourglassEmpty'
import { Box, Button, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useContext } from 'react'
import AppContext from '../../context/appContext'
import { isReportedByQueryLayer } from '../../utils/Errors/isReportedByQueryLayer'
import { findDCSByMetricEventId } from '../../utils/dataCollection/findDCSByMetricEventId'
import { useResolveFinding } from '../../hooks/data/useFindings'
import { findingsTypesConventions } from '../../utils/findingColors'
import FindingCommentsAccordion from './FindingCommentsAccordion'

const findingsTypes = [
  {
    label: 'Observación',
    color: findingsTypesConventions.observed.color,
  },
  {
    label: 'No Conformidad a Corregir',
    color: findingsTypesConventions.nonComplaintToFix.color,
  },
  {
    label: 'No Conformidad Grave',
    color: findingsTypesConventions.nonComplaintSevere.color,
  },
]

const FindingCard = ({ finding, readOnly, handleMetricReset }) => {
  const { userRoleSelected, setLoadingModalConfig, currentFarm } = useContext(AppContext)
  const resolveFindings = useResolveFinding()

  const reportResolveFailure = error =>
    setLoadingModalConfig({
      open: false,
      error,
      errorMessage: 'Error al resolver la observación, por favor intente nuevamente',
    })

  const handleResolveFinding = async () => {
    setLoadingModalConfig({
      open: true,
      dialogMessage: 'Guardando observación',
    })

    let dataCollectionStatement
    try {
      dataCollectionStatement = await findDCSByMetricEventId({
        farmId: currentFarm.id,
        userRole: userRoleSelected,
        metricEventId: finding.metricEventId,
      })
    } catch (error) {
      if (isReportedByQueryLayer(error)) setLoadingModalConfig({ open: false })
      else reportResolveFailure(error)
      return
    }

    try {
      if (!dataCollectionStatement) {
        throw new Error(
          'No se encontró la declaración de recolección de datos para esta observación',
        )
      }

      await resolveFindings.mutateAsync({
        userRole: userRoleSelected,
        dataCollectionStatementId: dataCollectionStatement.id,
        findingId: finding.id,
      })

      handleMetricReset()

      const successfulMessage = 'Observación resuelta exitosamente'
      setLoadingModalConfig({
        open: false,
        successfulMessage,
      })
    } catch (error) {
      reportResolveFailure(error)
    }
  }

  return (
    <Box
      key={finding.id}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        borderRadius: 2,
        padding: 1,
        margin: 0.5,
        backgroundColor: 'lightgray',
      }}
    >
      <Stack alignItems="center" direction="row" pb={0.5}>
        <Typography sx={{ fontWeight: 'bold' }} variant="body1">
          Tipo
        </Typography>
        <Typography variant="body1">{`: ${findingsTypes[finding.type]?.label ?? 'Desconocido'}`}</Typography>
        <Box
          backgroundColor={findingsTypes[finding.type]?.color ?? 'gray'}
          border="1px solid black"
          borderRadius="100%"
          height="16px"
          mx={0.5}
          width="16px"
        />
      </Stack>
      <Stack direction="column" pb={0.5}>
        <Typography
          sx={{
            fontWeight: 'bold',
            alignSelf: 'flex-start',
          }}
          variant="body1"
        >
          Campos afectados
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            '& > :not(style)': {
              m: 0.3,
            },
          }}
        >
          {finding.metricEventsFieldsObserved.map((field, index) => {
            return (
              <Chip
                // eslint-disable-next-line react/no-array-index-key
                key={field?.label ?? index}
                color="primary"
                label={field?.label ?? field}
                sx={{
                  height: '24px',
                }}
              />
            )
          })}
        </Box>
      </Stack>

      <Stack direction="column" pb={1} width="100%">
        <Typography
          sx={{
            fontWeight: 'bold',
            alignSelf: 'flex-start',
          }}
          variant="body1"
        >
          Descripción
        </Typography>
        <TextField
          multiline
          placeholder="Comentario"
          rows={2}
          slotProps={{ input: { readOnly: true } }}
          value={finding.comment}
          onChange={() => {}}
        />
      </Stack>

      <FindingCommentsAccordion finding={finding} />

      {readOnly ? (
        <Box
          mt={0.5}
          sx={{
            alignSelf: 'flex-end',
          }}
        >
          {!finding.resolved ? (
            <Tooltip title="En espera de resolución">
              <HourglassEmpty
                color="warning"
                sx={{
                  fontSize: 24,
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Resuelta">
              <CheckCircleOutline
                color="success"
                sx={{
                  fontSize: 28,
                }}
              />
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box
          mt={0.5}
          sx={{
            alignSelf: 'flex-end',
          }}
        >
          {!finding.resolved ? (
            <Button color="success" variant="contained" onClick={handleResolveFinding}>
              <Typography variant="body1">Resolver</Typography>
            </Button>
          ) : (
            <Tooltip title="Resuelta">
              <CheckCircleOutline
                color="success"
                sx={{
                  fontSize: 28,
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  )
}

export default FindingCard
